import { useState } from "react";
import { gql } from "@apollo/client";
import { extractLTDABTableData, extractABTableData } from "./ExcelExtractor";
import XLSX from "xlsx";
import { Alert } from "components/base/Toast";
import { normalizeItemNumber, normalizePrice } from "./utils";

const GET_PO_WITH_ITEMS = gql`
  query GetPOWithItems($poName: String!) {
    poWithItems(poName: $poName) {
      poId
      items {
        itemNumber
        qty
        unitPrice
        totalPrice
      }
      totalAmountCurrency
    }
  }
`;

export const useInvoiceFileUpload = (invoiceType = "LTD") => {
  const [extractedData, setExtractedData] = useState(null);
  const [loading, setLoading] = useState(false);

  const extractorFunction =
    invoiceType === "LTD" ? extractLTDABTableData : extractABTableData;

  const handleError = (message) => {
    Alert("error", message);
    setLoading(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "array" });

        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          raw: true,
        });

        const extractedResult = extractorFunction(jsonData);
        setExtractedData({
          items: extractedResult.items,
        });

        setLoading(false);
      } catch (error) {
        handleError(error.message);
      }
    };

    reader.onerror = () => handleError("File read error");
    reader.readAsArrayBuffer(file);
    e.target.value = null;
  };

  return { extractedData, loading, handleFileUpload };
};

// Custom hooks for LTD and AB invoice file uploads
export const useLTDInvoiceFileUpload = () => useInvoiceFileUpload("LTD");
export const useABInvoiceFileUpload = () => useInvoiceFileUpload("AB");

// Format PO items as a dictionary by itemNumber
const formatPOItemsData = (items) => {
  if (!items || !Array.isArray(items)) return null;

  const formattedData = {};
  items.forEach((item) => {
    if (item.itemNumber) {
      const normalizedItemNumber = normalizeItemNumber(item.itemNumber);
      formattedData[normalizedItemNumber] = {
        itemNumber: item.itemNumber,
        quantity: item.qty,
        unitPrice: normalizePrice(item.unitPrice),
        totalPrice: normalizePrice(item.totalPrice, 2),
      };
    }
  });
  return formattedData;
};

const isPoNotFoundError = (message = "") =>
  message.includes("Cannot return null for non-nullable field") ||
  message.includes("not found");

const formatPOError = (error, poName) => {
  if (!error) return null;

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.message || "Failed to load Odoo data";

  return isPoNotFoundError(errorMessage)
    ? `PO "${poName}" not found`
    : errorMessage;
};

const formatPOData = (data) =>
  data?.poWithItems
    ? {
        poId: data.poWithItems.poId,
        poItemsData: formatPOItemsData(data.poWithItems.items),
        odooTotalPrice: normalizePrice(data.poWithItems.totalAmountCurrency, 2),
      }
    : null;

const RETRIABLE_ERROR_KEYWORDS = [
  "network",
  "timeout",
  "failed to fetch",
  "fetch failed",
  "503",
  "504",
  "429",
  "service unavailable",
  "gateway timeout",
  "too many requests",
];

const isRetriableError = (error = "") => {
  const message = String(error || "").toLowerCase();
  return RETRIABLE_ERROR_KEYWORDS.some((keyword) => message.includes(keyword));
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const fetchPOData = async (
  client,
  poName,
  { maxRetries = 2, retryDelayMs = 500 } = {},
) => {
  if (!client || !poName) {
    return { data: null, error: null };
  }

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const result = await client.query({
        query: GET_PO_WITH_ITEMS,
        variables: { poName },
        fetchPolicy: "network-only",
        errorPolicy: "all",
      });

      let formattedError = formatPOError(result?.error, poName);
      if (!formattedError && Array.isArray(result?.errors) && result.errors[0]) {
        formattedError = formatPOError(result.errors[0]?.message, poName);
      }
      const formattedData = formatPOData(result?.data);

      if (!formattedData && !formattedError) {
        formattedError = `PO "${poName}" not found`;
      }

      const shouldRetry =
        !!formattedError &&
        isRetriableError(formattedError) &&
        attempt < maxRetries;
      if (shouldRetry) {
        await sleep(retryDelayMs * (attempt + 1));
        continue;
      }

      return {
        data: formattedData,
        error: formattedError,
      };
    } catch (error) {
      const formattedError = formatPOError(error, poName);
      const shouldRetry =
        isRetriableError(formattedError) && attempt < maxRetries;

      if (shouldRetry) {
        await sleep(retryDelayMs * (attempt + 1));
        continue;
      }

      return {
        data: null,
        error: formattedError,
      };
    }
  }

  return {
    data: null,
    error: "Failed to load Odoo data",
  };
};
